'use client';

import { resolveNumber, resolveText } from '@macaron-a2ui/core/v0_8';
import { resolveMacaronIconComponent, type MacaronIconStyle } from './macaronIconRegistry';
import { MACARON } from './tokens';
import type { MacaronRenderContext, RecordAny } from '@macaron-a2ui/core/v0_8';

function formatProgressNumber(value: number) {
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

export function MacaronCircularProgress({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const value = resolveNumber(config.value, ctx.data);
  const max = Math.max(1e-9, resolveNumber(config.max, ctx.data));
  const ratio = Math.max(0, Math.min(1, value / max));
  const style = typeof config.style === 'string' ? config.style : 'positive';
  const stroke = style === 'danger' ? MACARON.circularDanger : MACARON.circularPositive;
  const iconName = config.iconName ? resolveText(config.iconName, ctx.data) : '';
  const iconStyle = (typeof config.iconStyle === 'string' && (config.iconStyle === 'line' || config.iconStyle === 'filled') ? config.iconStyle : 'line') as MacaronIconStyle;
  const resolvedIcon = resolveMacaronIconComponent(iconName, iconStyle);
  // Flutter's A2UI CircularProgress only renders the icon when the name exists in its icon registry.
  // Unknown names fall back to numeric progress text; leaking the raw icon identifier into the center
  // makes debug payload details look like user-facing copy.
  const centerContent = `${formatProgressNumber(value)}/${formatProgressNumber(max)}`;

  const deg = ratio * 360;
  return (
    <div className="relative flex h-[120px] w-[120px] items-center justify-center">
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(${stroke} ${deg}deg, ${MACARON.circularTrack} 0)`,
        }}
      />
      <div className="absolute flex h-[104px] w-[104px] items-center justify-center rounded-full bg-white" style={{ margin: 8 }}>
        {resolvedIcon ? (
          <resolvedIcon.Component data-macaron-icon={resolvedIcon.canonicalName} data-macaron-icon-style={iconStyle} className="h-[42px] w-[42px] text-[#161615]" />
        ) : (
          <span className="font-sans text-[20px] font-semibold leading-none text-[#161615]">{centerContent}</span>
        )}
      </div>
    </div>
  );
}
